'use client'

import { useState } from 'react'
import {
  Button,
  Column,
  ContentSwitcher,
  Grid,
  Layer,
  Link,
  Switch,
  Tag,
  Tile,
} from '@carbon/react'
import { Checkmark, Help, View } from '@carbon/react/icons'
import type { KmerLocale } from '@kmerhosting/i18n'
import type { CatalogStatus, PricingCategory, PricingGroup } from '@/lib/catalog'
import type { PricingCopy } from '@/lib/pricing-i18n'

const pricingViews = ['All', 'Hosting', 'Domains', 'Email', 'Other'] as const
type PricingView = (typeof pricingViews)[number]
type PricingCategoryView = Exclude<PricingView, 'All'>
type ClientPricingCopy = Omit<PricingCopy, 'groupsShown'>

export type PricingEntry = {
  group: PricingGroup
  serviceSlug: string
  category: PricingCategory
  note: string | null
}

type PricingCatalogClientProps = {
  compact: boolean
  entries: PricingEntry[]
  locale: KmerLocale
  copy: ClientPricingCopy
}

function isUnavailable(status?: CatalogStatus) {
  return status === 'coming-soon' || status === 'out-of-stock'
}

function statusLabel(status: CatalogStatus | undefined, copy: ClientPricingCopy) {
  if (status === 'coming-soon') return copy.comingSoon
  if (status === 'out-of-stock') return copy.outOfStock
  return null
}

function localizedHref(href: string, locale: KmerLocale) {
  if (!href.startsWith('/') || href.startsWith('//') || locale === 'en') return href
  return `/${locale}${href}`
}

function localizedCadence(cadence: string, locale: KmerLocale) {
  if (cadence !== '/month') return cadence
  const monthly: Partial<Record<KmerLocale, string>> = {
    fr: '/mois', es: '/mes', pt: '/mês', de: '/Monat', 'zh-Hans': '/月', ar: '/شهر', hi: '/माह', bn: '/মাস', id: '/bulan', ja: '/月', ru: '/мес.', it: '/mese', ko: '/월', tr: '/ay', vi: '/tháng', ur: '/ماہ', nl: '/maand', pl: '/mies.', fa: '/ماه',
  }
  return monthly[locale] ?? cadence
}

function viewLabel(view: PricingView, copy: ClientPricingCopy) {
  if (view === 'All') return copy.all
  if (view === 'Hosting') return copy.hosting
  if (view === 'Domains') return copy.domains
  if (view === 'Email') return copy.email
  return copy.other
}

function PriceGroup({ entry, compact, locale, copy }: { entry: PricingEntry; compact: boolean; locale: KmerLocale; copy: ClientPricingCopy }) {
  const { group, serviceSlug, note } = entry
  const status = group.status ?? 'available'
  const localizedServiceName = copy.serviceLabels[serviceSlug]
  const showEnglishCatalogCopy = locale === 'en'

  return (
    <div className="price-group">
      <div className="price-group__intro">
        <div>
          <p className="section-label">{localizedServiceName ?? group.eyebrow}</p>
          <h3>{localizedServiceName ?? group.title}</h3>
        </div>
        {isUnavailable(status)
          ? <Tag type={status === 'out-of-stock' ? 'red' : 'blue'}>{statusLabel(status, copy)}</Tag>
          : <Link href={localizedHref(`/services/${serviceSlug}`, locale)} renderIcon={View}>{copy.serviceDetails}</Link>}
      </div>
      {showEnglishCatalogCopy && note ? <p className="pricing-note">{note}</p> : null}
      {isUnavailable(status) ? (
        <Button href={localizedHref('/support', locale)} kind="tertiary" renderIcon={Help}>{copy.supportOptions}</Button>
      ) : (
        <Layer>
          <div className="pricing-grid">
            {group.plans.map((plan) => {
              const features = compact ? (plan.previewFeatures ?? plan.features.slice(0, 3)) : plan.features
              return (
                <Tile key={`${group.title}-${plan.name}`} className="pricing-card">
                  <h4>{plan.name}</h4>
                  <strong>{plan.price}<small>{localizedCadence(plan.cadence, locale)}</small></strong>
                  {showEnglishCatalogCopy ? <p>{plan.description}</p> : null}
                  {showEnglishCatalogCopy ? (
                    <ul className="pricing-card__features" aria-label={`${plan.name} ${compact ? copy.featurePreview : copy.features}`}>
                      {features.map((feature) => (
                        <li key={feature}><Checkmark size={16} aria-hidden="true" />{feature}</li>
                      ))}
                    </ul>
                  ) : null}
                </Tile>
              )
            })}
          </div>
        </Layer>
      )}
    </div>
  )
}

export function PricingCatalogClient({ compact, entries, locale, copy }: PricingCatalogClientProps) {
  const [pricingView, setPricingView] = useState<PricingView>('All')
  const selectedCategory: PricingCategoryView | null = pricingView === 'All' ? null : pricingView
  const visiblePricing = selectedCategory
    ? entries.filter(({ category }) => category === selectedCategory)
    : entries

  return (
    <section id={compact ? 'pricing' : undefined} className="section pricing-section" aria-labelledby={compact ? 'pricing-title' : 'pricing-catalog-title'}>
      <Grid>
        <Column sm={4} md={8} lg={12}>
          <p className="section-label">{copy.label}</p>
          <h2 id={compact ? 'pricing-title' : 'pricing-catalog-title'} className="section__title">
            {compact ? copy.compactTitle : copy.fullTitle}
          </h2>
          <p className="section__subtitle">
            {compact ? copy.compactSubtitle : copy.fullSubtitle}
          </p>
          <ContentSwitcher
            className="pricing-switcher"
            selectedIndex={pricingViews.indexOf(pricingView)}
            onChange={({ name }) => {
              if (typeof name === 'string' && pricingViews.includes(name as PricingView)) {
                setPricingView(name as PricingView)
              }
            }}
            aria-label={copy.filterAria}
          >
            {pricingViews.map((view) => <Switch key={view} name={view} text={viewLabel(view, copy)} />)}
          </ContentSwitcher>
          <p className="sr-only" role="status" aria-live="polite">{visiblePricing.length} · {copy.label}</p>
          <div className="pricing-results">
            {visiblePricing.map((entry) => (
              <PriceGroup key={`${entry.serviceSlug}-${entry.group.title}`} entry={entry} compact={compact} locale={locale} copy={copy} />
            ))}
          </div>
          {compact ? (
            <Button href={localizedHref('/pricing', locale)} kind="tertiary" renderIcon={View}>{copy.viewAllPricing}</Button>
          ) : null}
        </Column>
      </Grid>
    </section>
  )
}
